/** ***********************************************************************
 *
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2019 Adobe Systems Incorporated
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe Systems Incorporated and its suppliers,
 * if any.  The intellectual and technical concepts contained
 * herein are proprietary to Adobe Systems Incorporated and its
 * suppliers and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe Systems Incorporated.
 ************************************************************************* */

import {
    CONFIRMATION_TYPES, CONFIRMATION_STATUS,
    CONFIRMATION_TYPES_TO_DIALOG_REPO_KEYS,
} from './confirmationDialogConstants';
import {
    AppDialog,
} from '../../../../stores/dialog';
import UninstallDialogModelMapper from './uninstallDialogModelMapper';
import UpdateDialogModelMapper from './updateDialogModelMapper';
import AutoUpdateDialogModelMapper from './autoUpdateDialogModelMapper';
import ConflictDialogModelMapper from './conflictDialogModelMapper';
import PIIModelMapper from './piiDialogModelMapper';
import RetryableErrorModelMapper from './retryableErrorModelMapper';
import CommonDialogModelMapper from './commonDialogModelMapper';
import { DialogAnalytics } from '../../../../analytics';
import LoggerUtil from '../../../LoggerUtil';
import LaunchConfirmationModelMapper from './launchConfirmationModelMapper';

class ConfirmationDialog {
    constructor() {
        this.dialogRepo = new Map();
    }

    updateDialogState(appData, onAction) {
        const { confirmationDetails } = appData;
        const { confirmationType } = confirmationDetails;
        const confirmationId = confirmationDetails.confirmationID;
        if (!this.dialogRepo.get(confirmationId)) {
            let updatedModel;
            DialogAnalytics.dialogRendered(appData);
            switch (confirmationType.toLowerCase()) {
            case CONFIRMATION_TYPES.UNINSTALL_PREFERENCES:
                updatedModel = UninstallDialogModelMapper.updateDialogRepo(appData);
                break;
            case CONFIRMATION_TYPES.FULL_APP_UPDATE_CONFIRMATION_WITH_ADVANCED_OPTIONS:
                updatedModel = UpdateDialogModelMapper.updateDialogRepo(appData, onAction, () => {
                    const appDialogDescription = UpdateDialogModelMapper
                        .getDescription(appData, onAction);
                    if (this.dialogRepo.get(confirmationId) && appDialogDescription) {
                        this.dialogRepo.get(confirmationId)
                            .updateDescriptionList(appDialogDescription);
                    }
                });
                break;
            case CONFIRMATION_TYPES.AUTO_UPDATE_WITH_ADVANCED_OPTIONS:
                updatedModel = AutoUpdateDialogModelMapper.updateDialogRepo(
                    appData, onAction, this.dialogRepo
                );
                break;
            case CONFIRMATION_TYPES.PREV_VER_WITH_DISABLED_AU:
            case CONFIRMATION_TYPES.DISABLE_AU_FOR_UNINSTALL:
            case CONFIRMATION_TYPES.DISABLE_AU_FOR_INSTALL:
            case CONFIRMATION_TYPES.PREV_VER:
            case CONFIRMATION_TYPES.ELEVATION_INFO:
                updatedModel = CommonDialogModelMapper.updateDialogRepo(
                    confirmationType.toLowerCase(), appData
                );
                break;
            case CONFIRMATION_TYPES.CONFLICTING_PROCESS:
                updatedModel = ConflictDialogModelMapper.updateDialogRepo(appData, onAction);
                break;
            case CONFIRMATION_TYPES.LAUNCH_CONFIRMATION:
                updatedModel = LaunchConfirmationModelMapper.updateDialogRepo(appData, onAction);
                break;
            case CONFIRMATION_TYPES.INCOMPATIBLE:
                LoggerUtil.log('incompatible');
                break;
            case CONFIRMATION_TYPES.PII:
                updatedModel = PIIModelMapper.updateDialogRepo(appData, onAction);
                break;
            case CONFIRMATION_TYPES.PRODUCT_UNAVAILABLE:
                LoggerUtil.log('productunavailable');
                break;
            case CONFIRMATION_TYPES.RETRYABLE_ERROR:
                updatedModel = RetryableErrorModelMapper.updateDialogRepo(appData);
                break;
            default:
                break;
            }
            if (updatedModel) {
                return this.updateModelData({
                    confirmationId,
                    confirmationType,
                    heading: updatedModel.heading,
                    description: updatedModel.description,
                    actionList: updatedModel.actionList,
                    inputList: updatedModel.inputList,
                    onAction: (actionId, operation) => {
                        DialogAnalytics.dialogCTAClicked(actionId, operation, appData);
                        onAction(actionId, operation);
                    },
                });
            }
            return null;
        }
        return this.dialogRepo.get(confirmationId);
    }

    updateModelData({
        confirmationId, confirmationType, heading, description, actionList, inputList, onAction,
    }) {
        let dialog = this.dialogRepo.get(confirmationId);
        const confirmationTypeL = confirmationType.toLowerCase();
        if (!dialog) {
            dialog = new AppDialog({
                type:
                    CONFIRMATION_TYPES_TO_DIALOG_REPO_KEYS[confirmationTypeL] || confirmationType,
            });
            this.dialogRepo.set(confirmationId, dialog);
        }
        if (dialog) {
            if (heading) {
                dialog.updateHeading(heading);
            }
            if (description) {
                dialog.updateDescriptionList(description);
            }
            if (actionList) {
                dialog.updateActionList(actionList);
            }
            if (inputList) {
                dialog.updateInputList(inputList);
            }
            if (onAction) {
                dialog.onAction = onAction;
            }
        }
        return dialog;
    }

    showOrHideDialog(appData) {
        if (appData.status && appData.confirmationDetails) {
            if (appData.status.toLowerCase() === CONFIRMATION_STATUS.NEED_CONFIRMATION) {
                const dialog = this.dialogRepo.get(appData.confirmationDetails.confirmationID);
                if (!dialog) {
                    return false;
                }
                dialog.show(appData.confirmationDetails.config);
                return true;
            }

            if (appData.status.toLowerCase() === CONFIRMATION_STATUS.CLOSE_CONFIRMATION) {
                return this.hideDialog(appData.confirmationDetails.confirmationID);
            }
        }
        return false;
    }

    hideDialog(confirmationId) {
        const dialog = this.dialogRepo.get(confirmationId);
        if (!dialog) {
            return false;
        }
        dialog.hide();
        this.dialogRepo.delete(confirmationId);
        return true;
    }
}
export default new ConfirmationDialog();
